import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api";
import { SpinnerGap, CalendarBlank, CheckCircle, Circle, ArrowSquareOut } from "@phosphor-icons/react";

// ── Utilitários de datas ────────────────────────────────────────────────────
const MS_DAY = 24 * 60 * 60 * 1000;
const MONTH_LABELS_PT = [
  "Jan", "Fev", "Mar", "Abr", "Mai", "Jun",
  "Jul", "Ago", "Set", "Out", "Nov", "Dez",
];

function parseDate(d) {
  if (!d) return null;
  // Aceita YYYY-MM-DD ou ISO
  const s = String(d).substring(0, 10);
  const [y, m, day] = s.split("-").map(Number);
  if (!y || !m || !day) return null;
  return new Date(Date.UTC(y, m - 1, day));
}

function startOfMonth(date) {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
}

function addMonths(date, n) {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + n, 1));
}

function monthsBetween(start, end) {
  return (end.getUTCFullYear() - start.getUTCFullYear()) * 12 + (end.getUTCMonth() - start.getUTCMonth());
}

// Gera lista de meses [{ year, month, label, startMs }] entre start e end (inclusive)
function buildMonthList(start, end) {
  const list = [];
  let cursor = startOfMonth(start);
  const last = startOfMonth(end);
  while (cursor <= last) {
    list.push({
      year: cursor.getUTCFullYear(),
      month: cursor.getUTCMonth() + 1,
      label: MONTH_LABELS_PT[cursor.getUTCMonth()],
      startMs: cursor.getTime(),
    });
    cursor = addMonths(cursor, 1);
  }
  return list;
}

// ── Estilos cromáticos por sonho (estáveis por ID) ─────────────────────────
const PALETTE = [
  "#6F4F1D", "#0F1829", "#7C2D6F", "#1E5E54",
  "#A24A20", "#3B5598", "#7A4D4D", "#1F6F3B",
];
function colorForSonho(id) {
  return PALETTE[Number(id) % PALETTE.length];
}

// ─────────────────────────────────────────────────────────────────────────────
// Página Timeline — régua temporal de anos/meses com sonhos como barras
// e tarefas sincronizadas por baixo pelo `Data`.
// ─────────────────────────────────────────────────────────────────────────────
export default function DreamsTimeline() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/timeline")
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, []);

  // Constrói a régua + posicionamento dos sonhos/tarefas
  const grid = useMemo(() => {
    if (!data?.sonhos?.length || !data?.range) return null;
    const start = startOfMonth(parseDate(data.range.start) || new Date());
    const endRaw = parseDate(data.range.end) || new Date();
    const end = addMonths(startOfMonth(endRaw), 1); // padding de 1 mês

    const months = buildMonthList(start, end);
    const totalMonths = months.length;
    if (totalMonths <= 0) return null;
    const totalMs = end.getTime() - start.getTime();

    const positionPct = (date) => {
      const d = parseDate(date);
      if (!d) return null;
      const offset = d.getTime() - start.getTime();
      return Math.max(0, Math.min(100, (offset / totalMs) * 100));
    };

    return { months, totalMonths, start, end, positionPct };
  }, [data]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="timeline-loading">
        <SpinnerGap size={32} className="animate-spin text-[#6F4F1D]" />
      </div>
    );
  }

  if (err) {
    return <p className="text-red-700 text-sm" data-testid="timeline-err">{err}</p>;
  }

  if (!data?.sonhos?.length) {
    return (
      <div className="text-center py-16">
        <CalendarBlank size={48} className="mx-auto text-[#6F4F1D]/40" />
        <h2 className="text-xl mt-4 font-semibold text-[#0F1829]" style={{ fontFamily: "'Fraunces', serif" }}>Sem sonhos para mostrar</h2>
        <p className="text-sm text-[#978D78] mt-2">Cria o teu primeiro sonho para ver a tua timeline.</p>
        <Link to="/dreams/sonhos" data-testid="timeline-empty-cta"
              className="inline-block mt-4 px-5 py-2 rounded-full bg-[#0F1829] text-white text-sm hover:bg-[#001f6e]">
          Criar sonho
        </Link>
      </div>
    );
  }

  if (!grid) {
    return (
      <div className="text-center py-16">
        <p className="text-sm text-[#978D78]">Adiciona datas (início e alvo) aos teus sonhos para os ver na timeline.</p>
      </div>
    );
  }

  const { months, positionPct } = grid;
  // Largura mínima por mês para legibilidade. Scroll horizontal quando excede o container.
  const MONTH_WIDTH_PX = 80;
  const rulerWidth = months.length * MONTH_WIDTH_PX;

  // Posição da linha "Hoje" — null se hoje cair fora do range visível
  const today = new Date();
  const todayIso = `${today.getUTCFullYear()}-${String(today.getUTCMonth() + 1).padStart(2, "0")}-${String(today.getUTCDate()).padStart(2, "0")}`;
  const todayPct = positionPct(todayIso);
  const todayInsideRange = todayPct != null && todayPct > 0 && todayPct < 100;
  const todayLabel = today.toLocaleDateString("pt-PT", { day: "2-digit", month: "short", year: "numeric" });

  // Agrupa meses por ano para o cabeçalho de 2 níveis
  const years = [];
  months.forEach((m) => {
    const last = years[years.length - 1];
    if (last && last.year === m.year) last.count += 1;
    else years.push({ year: m.year, count: 1 });
  });

  return (
    <div className="space-y-6" data-testid="timeline-root">
      <header>
        <h1 className="text-2xl sm:text-3xl font-semibold text-[#0F1829]" style={{ fontFamily: "'Fraunces', serif" }}>
          Timeline
        </h1>
        <p className="text-sm text-[#978D78] mt-1">
          Visualização cronológica dos sonhos com as tarefas sincronizadas pela régua temporal.
        </p>
      </header>

      <div className="rounded-2xl border border-[#E6E1D6] bg-white p-4 overflow-x-auto">
        <div style={{ minWidth: rulerWidth }} className="relative">
          {/* Linha vertical "Hoje" — corre por toda a altura das faixas */}
          {todayInsideRange && (
            <div
              className="absolute top-0 bottom-0 pointer-events-none z-20"
              style={{ left: `${todayPct}%` }}
              data-testid="tl-today-line"
            >
              <div
                className="absolute top-0 bottom-0 w-px"
                style={{ background: "#C2410C", boxShadow: "0 0 0 0.5px rgba(194,65,12,0.25)" }}
              />
              <div
                className="absolute -top-1 -translate-x-1/2 px-1.5 py-0.5 rounded-md text-[9px] font-semibold uppercase tracking-wider text-white whitespace-nowrap shadow-sm"
                style={{ background: "#C2410C" }}
                title={`Hoje · ${todayLabel}`}
              >
                Hoje
              </div>
            </div>
          )}
          {/* Régua: anos */}
          <div className="flex border-b border-[#E6E1D6]">
            {years.map((y) => (
              <div
                key={y.year}
                style={{ width: y.count * MONTH_WIDTH_PX }}
                className="text-center py-1.5 text-xs font-semibold text-[#0F1829] border-r border-[#E6E1D6] last:border-r-0 bg-[#F5F2EB]"
                data-testid={`tl-year-${y.year}`}
              >
                {y.year}
              </div>
            ))}
          </div>
          {/* Régua: meses */}
          <div className="flex border-b-2 border-[#0F1829]/10">
            {months.map((m, i) => (
              <div
                key={`${m.year}-${m.month}`}
                style={{ width: MONTH_WIDTH_PX }}
                className={`text-center py-1 text-[10px] uppercase tracking-wider text-[#6B7280] border-r border-[#E6E1D6] last:border-r-0 ${i % 2 === 0 ? "bg-[#FAF8F3]" : "bg-white"}`}
              >
                {m.label}
              </div>
            ))}
          </div>

          {/* Linhas por sonho */}
          <div className="divide-y divide-[#F5F2EB]">
            {data.sonhos.map((s) => {
              const color = colorForSonho(s.ID_Sonho);
              const startPct = positionPct(s.DataInicio);
              const endPct = positionPct(s.DataAlvo);
              const hasRange = startPct != null && endPct != null && endPct >= startPct;
              return (
                <div key={s.ID_Sonho} className="relative py-3" data-testid={`tl-sonho-${s.ID_Sonho}`}>
                  {/* Título sticky-left para legibilidade ao scroll horizontal */}
                  <div className="flex items-center gap-2 mb-1.5 sticky left-0 z-10 bg-white pr-2" style={{ width: 360, maxWidth: "60%" }}>
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: color }} />
                    <Link to={`/dreams/sonhos/${s.ID_Sonho}`}
                          className="text-sm font-medium text-[#0F1829] truncate hover:underline"
                          title={s.Titulo}
                          data-testid={`tl-sonho-title-${s.ID_Sonho}`}>
                      {s.Titulo}
                    </Link>
                    {s.Estado === "CO" && <CheckCircle size={14} weight="fill" className="text-emerald-600 shrink-0" />}
                    <ArrowSquareOut size={11} className="text-[#978D78] shrink-0" />
                  </div>

                  {/* Faixa do sonho */}
                  <div className="relative h-6 rounded-full bg-[#F5F2EB] overflow-visible">
                    {hasRange && (
                      <div
                        className="absolute top-0 bottom-0 rounded-full border opacity-90"
                        style={{
                          left: `${startPct}%`,
                          width: `${Math.max(0.5, endPct - startPct)}%`,
                          background: `${color}22`,
                          borderColor: `${color}88`,
                        }}
                        title={`${s.DataInicio?.slice(0,10) || "?"} → ${s.DataAlvo?.slice(0,10) || "?"}`}
                      />
                    )}
                    {/* Marcadores de DataInicio e DataAlvo */}
                    {startPct != null && (
                      <span
                        className="absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full ring-2 ring-white shadow"
                        style={{ left: `calc(${startPct}% - 5px)`, background: color }}
                        title={`Início: ${s.DataInicio?.slice(0,10)}`}
                      />
                    )}
                    {endPct != null && (
                      <span
                        className="absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full ring-2 ring-white shadow"
                        style={{ left: `calc(${endPct}% - 5px)`, background: color, opacity: 0.65 }}
                        title={`Alvo: ${s.DataAlvo?.slice(0,10)}`}
                      />
                    )}
                  </div>

                  {/* Tarefas sincronizadas */}
                  {s.tarefas?.length > 0 && (
                    <div className="relative h-7 mt-1.5">
                      {s.tarefas.map((t) => {
                        const x = positionPct(t.Data);
                        if (x == null) return null;
                        const done = t.Estado === "CO";
                        return (
                          <div
                            key={t.ID_Tarefa}
                            className="absolute top-1.5 group"
                            style={{ left: `calc(${x}% - 7px)` }}
                            data-testid={`tl-task-${t.ID_Tarefa}`}
                          >
                            {done ? (
                              <CheckCircle size={14} weight="fill" className="text-emerald-600" />
                            ) : (
                              <Circle size={14} weight="bold" style={{ color }} />
                            )}
                            <div className="hidden group-hover:block absolute z-20 left-1/2 -translate-x-1/2 top-5 whitespace-nowrap px-2 py-1 rounded-md bg-[#0F1829] text-white text-[10px] shadow-lg">
                              {(t.Data || "").slice(0,10)} · {t.Titulo}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Legenda */}
      <div className="flex flex-wrap items-center gap-4 text-[11px] text-[#978D78]">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-full bg-[#C2410C]" /> Hoje
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Circle size={10} weight="bold" /> Tarefa pendente
        </span>
        <span className="inline-flex items-center gap-1.5">
          <CheckCircle size={10} weight="fill" className="text-emerald-600" /> Tarefa concluída
        </span>
      </div>
    </div>
  );
}
